import {isNil} from 'lodash-es';
import {DAYS_WITH_LABELS} from 'utils/date-time';
import {Yup} from 'utils/validators';
import {
    addTime,
    convertTimeToDecimal,
    parse48HourClockTime,
    INTERPOLATION_ALGORITHMS,
} from '../utils/utils';

/**
 * Converts validation errors into a formatted object keyed by the input label.
 *
 * @param {import('yup').ValidationError} err - The Yup validation error object.
 * @returns {Object} - The formatted validation errors.
 *
 * @example
 * const formattedErrors = formatValidationErrors(err); // {maxRate: 'Max rate is required.', minRate: 'Min rate is required.', ...}
 */
export const formatValidationErrors = (err) =>
    err.inner.reduce((errors, error) => {
        const {params, message} = error;

        errors[params.label] = message;

        return errors;
    }, {});

/**
 * An object containing various error messages for flex-rate related issues.
 *
 * @type {object}
 * @const
 *
 * @property {string} moreThanLessThan - Message to show when rates are either less than $0 or more than $250.
 *
 * @property {object} rateBands - Rate-band specific error messages.
 * @property {string} rateBands.compareDays - Message to indicate that higher rates are required for longer durations.
 *
 * @property {object} rateSegments - Rate-segment specific error messages.
 * @property {string} rateSegments.maxGreaterThanMin - Message to show when a maximum rate is not greater than or equal to the minimum rate.
 * @property {function} rateSegments.exitBefore - Function to generate an error message based on provided minRange and maxRange values.
 * @property {string} rateSegments.overlap - Message to show when rate segments overlap in terms of days or hours.
 * @property {string} rateSegments.customNameLength - Message to show when a custom name is greater than 50 characters.
 *
 * @property {object} required - Error messages related to required field errors.
 * @property {string} required.daysOfWeek - Message to show when rate bands do not apply to at least one day of the week.
 * @property {string} required.minMaxRate - Message to show when a minimum and maximum rate are not set.
 * @property {string} required.duration24hr - Message to show when a 24 hour rate has not been set for each day of the week.
 * @property {string} required.customName - Message to show when a custom name is not set.
 */
export const ERROR_MESSAGE_FLEX_RATES = {
    moreThanLessThan: 'Rates must be greater than $0 and cannot exceed $250.',
    rateBands: {
        compareDays: 'Higher rates are required for longer durations.',
    },
    rateSegments: {
        maxGreaterThanMin:
            'Maximum rate must be greater than or equal to minimum rate.',
        exitBefore: ({minRange, maxRange}) =>
            `'Exit Before' time must fall between ${parse48HourClockTime(
                minRange.toString()
            )} and ${parse48HourClockTime(
                maxRange.toString()
            )} based on the start time and duration inputs provided below.`,
        overlap:
            'Unable to add/edit due to overlapping time frames with one or more existing Special Rate Bands. Adjust the days of the week and/or rental times and try again.',
        customNameLength: 'Maximum character length is 50',
    },
    required: {
        customName: 'Rate Name is required.',
        daysOfWeek: 'Rate band must apply to at least one day of the week.',
        minMaxRate: 'Must set both a minimum and maximum rate.',
        duration24hr: '24 hr rate required for each day of the week.',
    },
};

/**
 * An object containing error messages for multi-day discounts.
 *
 * @type {object}
 * @const
 * @property {string} moreThanLessThan - Message to show when discount percentages are not between 0% and 99%.
 * @property {string} integersOnly - Message to show when the discount percentage is not a whole number.
 * @property {object} required - Object containing required field error messages.
 * @property {string} required.amount - Message to show when the discount amount is not set.
 * @property {string} required.duration - Message to show when the duration is not set.
 */
export const ERROR_MESSAGE_MULTI_DAY_DISCOUNTS = {
    moreThanLessThan: 'Discounts must be between 0%-99%.',
    integersOnly: 'Discount percentage must be a whole number.',
    required: {
        amount: 'Discount amount is required.',
        duration: 'Duration is required.',
    },
};

/**
 * Handles the required duration validation
 * @param {string} duration
 * @param {import('yup').AnySchema} schema - The Yup schema to apply the required validation to
 * @return {import('yup').AnySchema}
 */
const handleRequiredDuration = (duration, schema) => {
    if (duration === '24:00') {
        return schema.required(ERROR_MESSAGE_FLEX_RATES.required.duration24hr);
    }
};

/**
 * A Yup schema that validates a collection of rate thresholds
 * @async
 * @return {(import('yup').ValidationError|TFlexRateFormattedRateBand[])}
 */
export const rateBandsSchema = Yup.array().of(
    Yup.object({
        monday: Yup.number()
            .nullable()
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .when(['duration'], handleRequiredDuration),
        tuesday: Yup.number()
            .nullable()
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .when(['duration'], handleRequiredDuration),
        wednesday: Yup.number()
            .nullable()
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .when(['duration'], handleRequiredDuration),
        thursday: Yup.number()
            .nullable()
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .when(['duration'], handleRequiredDuration),
        friday: Yup.number()
            .nullable()
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .when(['duration'], handleRequiredDuration),
        saturday: Yup.number()
            .nullable()
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .when(['duration'], handleRequiredDuration),
        sunday: Yup.number()
            .nullable()
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .when(['duration'], handleRequiredDuration),
        daysOfWeek: Yup.array()
            .label('daysOfWeek')
            .notRequired()
            .min(1, ERROR_MESSAGE_FLEX_RATES.required.daysOfWeek),
        rate: Yup.number()
            .label('rate')
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .notRequired(),
    })
        .comparePropertiesInCollection(
            ERROR_MESSAGE_FLEX_RATES.rateBands.compareDays,
            (value, item) => {
                const isSameObject = value.duration === item.duration;

                // loop over all the all the days of the week
                const result = Object.keys(DAYS_WITH_LABELS).some((day) => {
                    const hasRate = !isNil(value[day]) && !isNil(item[day]);

                    return (
                        // check if a rate is present for both days
                        hasRate &&
                        // make sure we're not comparing the same object
                        !isSameObject &&
                        // make sure we're only checking durations that are greater than the current duration
                        value.duration < item.duration &&
                        // return true if the rate for the current day is greater than the rate for the same day in the next duration
                        value[day] > item[day]
                    );
                });

                return result;
            }
        )
        .label('rateBandsCompareDays')
);

/**
 * A Yup schema that validates a collection of rate segments
 * @async
 */
export const rateSegmentsSchema = Yup.array().of(
    Yup.object({
        customName: Yup.string()
            .max(79, ERROR_MESSAGE_FLEX_RATES.rateSegments.customNameLength)
            .label('customName')
            .required(ERROR_MESSAGE_FLEX_RATES.required.customName),
        triggerTime: Yup.number()
            .transform((value, originalValue, context) => {
                // if the value is already cast to the correct type, return it
                if (context.isType(value)) {
                    return value;
                }

                // otherwise convert the time to decimal so we can accurate compare it to the min & max range
                return convertTimeToDecimal(originalValue);
            })
            .label('triggerTime')
            .when(
                [
                    'latestEntryTime',
                    'earliestEntryTime',
                    'maxDuration',
                    'minDuration',
                ],
                (
                    latestEntryTime,
                    earliestEntryTime,
                    maxDuration,
                    minDuration,
                    schema
                ) => {
                    // calculate the min & maxRange to compare against the triggerTime.
                    const maxRange = addTime(
                        latestEntryTime,
                        parseInt(maxDuration, 10)
                    );
                    const minRange = addTime(
                        earliestEntryTime,
                        parseInt(minDuration, 10)
                    );

                    return schema.moreThan(
                        convertTimeToDecimal(minRange),
                        ERROR_MESSAGE_FLEX_RATES.rateSegments.exitBefore({
                            minRange,
                            maxRange,
                        })
                    );
                }
            )
            .when(
                [
                    'latestEntryTime',
                    'earliestEntryTime',
                    'maxDuration',
                    'minDuration',
                ],
                (
                    latestEntryTime,
                    earliestEntryTime,
                    maxDuration,
                    minDuration,
                    schema
                ) => {
                    // calculate the maxRange to compare against the triggerTime
                    const maxRange = addTime(
                        latestEntryTime,
                        parseInt(maxDuration, 10)
                    );
                    const minRange = addTime(
                        earliestEntryTime,
                        parseInt(minDuration, 10)
                    );

                    const maxRangeDecimal =
                        convertTimeToDecimal(maxRange) + 0.1; // adds a fraction to the upper range, so that maximum exit time is included, allowed, and valid

                    return schema.lessThan(
                        maxRangeDecimal,
                        ERROR_MESSAGE_FLEX_RATES.rateSegments.exitBefore({
                            minRange,
                            maxRange,
                        })
                    );
                }
            ),
        daysOfWeek: Yup.array()
            .label('daysOfWeek')
            .required(ERROR_MESSAGE_FLEX_RATES.required.daysOfWeek)
            // we use min here for testing purposes because jest doesn't like the "required" validation on arrays
            .min(1, ERROR_MESSAGE_FLEX_RATES.required.daysOfWeek),
        minRate: Yup.number()
            .nullable()
            .label('minRate')
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .when(['interpolation'], (interpolation, schema) => {
                if (interpolation !== INTERPOLATION_ALGORITHMS.none) {
                    return schema.required(
                        ERROR_MESSAGE_FLEX_RATES.required.minMaxRate
                    );
                }
            })
            .when(['maxRate'], (maxRate, schema) => {
                return schema.max(
                    maxRate,
                    ERROR_MESSAGE_FLEX_RATES.rateSegments.maxGreaterThanMin
                );
            }),
        maxRate: Yup.number()
            .nullable()
            .label('maxRate')
            .max(250, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .min(0, ERROR_MESSAGE_FLEX_RATES.moreThanLessThan)
            .required(ERROR_MESSAGE_FLEX_RATES.required.minMaxRate),
    })
        .comparePropertiesInCollection(
            ERROR_MESSAGE_FLEX_RATES.rateSegments.overlap,
            (value, item) => {
                // if we're comparing the same objects, skip the comparison
                const isSameObject = value.customRateId === item.customRateId;
                // check if the entry times overlap.  We use parseInt here because we don't want to cast the values to numbers in the schema
                const entryStart1 = parseInt(value.earliestEntryTime, 10);
                const entryEnd1 = parseInt(value.latestEntryTime, 10);
                const durationMin1 = parseInt(value.minDuration, 10);
                const durationMax1 = parseInt(value.maxDuration, 10);
                const entryStart2 = parseInt(item.earliestEntryTime, 10);
                const entryEnd2 = parseInt(item.latestEntryTime, 10);
                const durationMin2 = parseInt(item.minDuration, 10);
                const durationMax2 = parseInt(item.maxDuration, 10);

                // exclude triggered rate segments, then check if the entry times overlap & if the duration overlaps.  If both are true, then the rate segments overlap
                const isOverlap =
                    !value.triggerTime &&
                    entryStart1 < entryEnd2 &&
                    entryStart2 < entryEnd1 &&
                    ((durationMin1 < durationMax2 &&
                        durationMin2 < durationMax1) ||
                        durationMin1 === durationMin2);

                // check if the days of week overlap
                const daysOverlap = value?.daysOfWeek?.some((day) => {
                    return item?.daysOfWeek?.includes(day);
                });

                return !isSameObject && daysOverlap && isOverlap;
            }
        )
        .label('rateSegmentsOverlap')
);

/**
 * A Yup schema that validates a collection of multi-day discounts
 * @return
 */
export const multiDayDiscountsSchema = Yup.object({
    duration: Yup.string()
        .label('duration')
        .required(ERROR_MESSAGE_MULTI_DAY_DISCOUNTS.required.duration),
    amount: Yup.number()
        .label('amount')
        // eslint-disable-next-line no-undefined
        .transform((value) => (isNaN(value) ? undefined : value))
        .required(ERROR_MESSAGE_MULTI_DAY_DISCOUNTS.required.amount)
        .moreThan(-1, ERROR_MESSAGE_MULTI_DAY_DISCOUNTS.moreThanLessThan)
        .max(99, ERROR_MESSAGE_MULTI_DAY_DISCOUNTS.moreThanLessThan)
        .integer(ERROR_MESSAGE_MULTI_DAY_DISCOUNTS.integersOnly),
    example: Yup.object({
        discounted: Yup.number().label('discounted'),
        duration: Yup.string().label('duration'),
        original: Yup.number().label('original'),
        startDay: Yup.string().label('startDay'),
        startTime: Yup.string().label('startTime'),
    }),
}).label('multiDayDiscounts');
